import { Box, List, Collapse } from '@mui/material';
import React, { useMemo, useState, useCallback, useEffect, useRef, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import routes from '../../routes/routes';
import { defaultSelectedMenu } from '../../config/appConfig';
import { appTypes } from '../../redux/types';
import './LeftNav.css';
import { useNavigate, useLocation } from 'react-router-dom';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { getSideMenus } from '../../config/navigation';
import useAuth from '../../hooks/useAuth';
import { AUTH_USER_TYPES } from '../../constants/auth.constants';

const MENU_HOVER_OPEN_MS = 40;
const MENU_HOVER_CLOSE_MS = 160;

const LeftNav = ({ open }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { userType, activeTenantId, activeTenantName } = useAuth();
    const selectedLeftMenu = useSelector(
        (state) => state?.app?.selectedLeftMenu || defaultSelectedMenu.left
    );
    const sideMenus = useMemo(
        () =>
            getSideMenus(routes, userType || AUTH_USER_TYPES.SYSTEM, {
                activeTenantId,
                activeTenantName,
            }),
        [activeTenantId, activeTenantName, userType]
    );

    const [hoveredMenuIndex, setHoveredMenuIndex] = useState(null);
    const [clickedOpenMenus, setClickedOpenMenus] = useState({});
    const hoverOpenTimerRef = useRef(null);
    const hoverCloseTimerRef = useRef(null);

    const clearMenuHoverTimers = useCallback(() => {
        if (hoverOpenTimerRef.current) {
            clearTimeout(hoverOpenTimerRef.current);
            hoverOpenTimerRef.current = null;
        }
        if (hoverCloseTimerRef.current) {
            clearTimeout(hoverCloseTimerRef.current);
            hoverCloseTimerRef.current = null;
        }
    }, []);

    useEffect(() => () => clearMenuHoverTimers(), [clearMenuHoverTimers]);

    useEffect(() => {
        const currentPath = location.pathname.replace(/^\/+/, "");
        const match =
            sideMenus.find((item) => item.path === currentPath) ||
            sideMenus
                .flatMap((item) => item.subItems || [])
                .find((item) => item.path === currentPath);
        if (match?.id && match.id !== selectedLeftMenu) {
            dispatch({ type: appTypes.SELECT_LEFT_MENU, payload: match.id });
        }
    }, [dispatch, location.pathname, selectedLeftMenu, sideMenus]);

    useEffect(() => {
        if (!open) {
            clearMenuHoverTimers();
            setHoveredMenuIndex(null);
        }
    }, [open, clearMenuHoverTimers]);

    const handleMenuHoverEnter = useCallback(
        (index, hasSubItems) => {
            if (!open || !hasSubItems) return;
            if (hoverCloseTimerRef.current) {
                clearTimeout(hoverCloseTimerRef.current);
                hoverCloseTimerRef.current = null;
            }
            if (hoverOpenTimerRef.current) {
                clearTimeout(hoverOpenTimerRef.current);
                hoverOpenTimerRef.current = null;
            }
            hoverOpenTimerRef.current = setTimeout(() => {
                setHoveredMenuIndex(index);
            }, MENU_HOVER_OPEN_MS);
        },
        [open]
    );

    const handleMenuHoverLeave = useCallback((index) => {
        if (hoverOpenTimerRef.current) {
            clearTimeout(hoverOpenTimerRef.current);
            hoverOpenTimerRef.current = null;
        }
        hoverCloseTimerRef.current = setTimeout(() => {
            setHoveredMenuIndex((current) => (current === index ? null : current));
        }, MENU_HOVER_CLOSE_MS);
    }, []);

    const handleMenuItemClick = useCallback(
        (event, menuItem, index) => {
            event.stopPropagation();
            const hasPath = Boolean(menuItem.path && menuItem.path !== "/");
            const hasSubItems = Boolean(menuItem?.subItems?.length);

            // Parent groups (System / Tenants) toggle open on click.
            if (hasSubItems && !hasPath) {
                const menuId = menuItem.id || String(index);
                const currentPath = location.pathname.replace(/^\/+/, "");
                const routeExpanded =
                    menuItem?.subItems?.some(
                        (subItem) => subItem.id === selectedLeftMenu
                    ) ||
                    (menuItem?.id === "tenant-scope" &&
                        Boolean(activeTenantId) &&
                        (currentPath === "dashboard" || currentPath === ""));

                setClickedOpenMenus((prev) => {
                    const currentlyOpen =
                        hoveredMenuIndex === index ||
                        Boolean(prev[menuId]) ||
                        (routeExpanded && prev[menuId] !== false);
                    const nextOpen = !currentlyOpen;

                    if (nextOpen) {
                        setHoveredMenuIndex(index);
                    } else {
                        setHoveredMenuIndex((current) =>
                            current === index ? null : current
                        );
                    }

                    return {
                        ...prev,
                        [menuId]: nextOpen,
                    };
                });
                return;
            }

            if (menuItem.id) {
                dispatch({ type: appTypes.SELECT_LEFT_MENU, payload: menuItem.id });
            }
            if (hasPath) {
                navigate("/" + menuItem.path.replace(/^\/+/, ""));
            }
        },
        [activeTenantId, dispatch, hoveredMenuIndex, location.pathname, navigate, selectedLeftMenu]
    );

    const isParentExpanded = useCallback(
        (index, menuItem) => {
            const menuId = menuItem?.id || String(index);

            // Hover expands this menu without forcing others closed (avoids
            // layout-shift canceling System expand while Tenants is open).
            if (hoveredMenuIndex === index) {
                return true;
            }

            if (clickedOpenMenus[menuId]) {
                return true;
            }

            if (menuItem?.subItems?.some((subItem) => subItem.id === selectedLeftMenu)) {
                return true;
            }

            const currentPath = location.pathname.replace(/^\/+/, "");
            return (
                menuItem?.id === "tenant-scope" &&
                Boolean(activeTenantId) &&
                (currentPath === "dashboard" || currentPath === "") &&
                clickedOpenMenus[menuId] !== false
            );
        },
        [
            activeTenantId,
            clickedOpenMenus,
            hoveredMenuIndex,
            location.pathname,
            selectedLeftMenu,
        ]
    );

    const isMenuActive = useCallback(
        (menuItem) => {
            const hasSelectedSubItem = menuItem?.subItems?.some(
                (subItem) => subItem.id === selectedLeftMenu
            );
            return Boolean(menuItem.id === selectedLeftMenu || hasSelectedSubItem);
        },
        [selectedLeftMenu]
    );

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                py: 1.5,
                overflowX: "hidden",
            }}
        >
            <List
                sx={{
                    px: open ? 1 : 0,
                    pb: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    flexGrow: 1,
                }}
            >
                {sideMenus.map((menuItem, index) => {
                    const active = isMenuActive(menuItem);
                    const parentOpen = isParentExpanded(index, menuItem);
                    const hasSubItems = Boolean(menuItem?.subItems?.length);

                    return (
                        <Box
                            key={menuItem.id || menuItem.name || index}
                            onMouseEnter={() => handleMenuHoverEnter(index, hasSubItems)}
                            onMouseLeave={() => handleMenuHoverLeave(index)}
                        >
                            <ListItem
                                disablePadding
                                sx={{
                                    display: "flex",
                                    justifyContent: open ? "flex-start" : "center",
                                }}
                                selected={active}
                                onClick={(event) =>
                                    handleMenuItemClick(event, menuItem, index)
                                }
                                className={open && active ? "active-menu" : ""}
                            >
                                <ListItemButton
                                    disableGutters
                                    className={`nav-item-button parent-item ${open ? "expanded" : "collapsed"} ${active ? "active-menu" : ""}`}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        minHeight: open ? 52 : 44,
                                        justifyContent: open ? "flex-start" : "center",
                                        px: open ? 1.75 : 0,
                                        borderRadius: 2,
                                        mb: 0.5,
                                        width: open ? "100%" : 42,
                                        mx: open ? 0 : "auto",
                                        transition: "background-color 120ms ease",
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: open ? 0 : 24,
                                            mr: open ? 2 : 0,
                                            justifyContent: "center",
                                            alignItems: "center",
                                            display: "flex",
                                            width: open ? "auto" : 24,
                                            height: open ? "auto" : 24,
                                        }}
                                        className={active ? "active-icon" : ""}
                                    >
                                        {menuItem.icon}
                                    </ListItemIcon>
                                    {open ? (
                                        <ListItemText
                                            primary={menuItem.label}
                                            primaryTypographyProps={{
                                                fontSize: 14,
                                                fontWeight: menuItem.id === selectedLeftMenu ? 700 : 500,
                                            }}
                                        />
                                    ) : null}
                                    {open && hasSubItems ? (
                                        <Box sx={{ ml: 1, display: "flex" }}>
                                            {parentOpen ? <ExpandLess /> : <ExpandMore />}
                                        </Box>
                                    ) : null}
                                </ListItemButton>
                            </ListItem>

                            {hasSubItems && open ? (
                                <Collapse
                                    in={parentOpen}
                                    timeout={140}
                                    unmountOnExit
                                >
                                    <List component="div" disablePadding sx={{ mt: 0.25, mb: 0.5 }}>
                                        {menuItem.subItems.map((subItem) => {
                                            const subActive = subItem.id === selectedLeftMenu;
                                            return (
                                                <ListItemButton
                                                    key={subItem.id || subItem.path}
                                                    disableGutters
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: open ? "flex-start" : "center",
                                                        px: open ? 1 : 0,
                                                        pl: open ? 4.5 : 0,
                                                        minHeight: open ? 42 : 34,
                                                        borderRadius: 2,
                                                        mx: open ? 0.75 : "auto",
                                                        ml: open ? 0.75 : 0,
                                                        mb: open ? 0.5 : 0.25,
                                                        width: open ? "auto" : 28,
                                                        transition: "background-color 120ms ease",
                                                    }}
                                                    onClick={(event) =>
                                                        handleMenuItemClick(event, subItem, index)
                                                    }
                                                    selected={subActive}
                                                    className={`nav-item-button submenu-item ${open ? "expanded" : "collapsed"} ${subActive ? "active-menu" : ""}`}
                                                >
                                                    <ListItemIcon
                                                        sx={{
                                                            minWidth: open ? 0 : 24,
                                                            mr: open ? 1.75 : 0,
                                                            justifyContent: "center",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            width: open ? "auto" : 20,
                                                            height: open ? "auto" : 20,
                                                        }}
                                                        className={
                                                            subActive
                                                                ? open
                                                                    ? "active-icon"
                                                                    : "active-sub-icon"
                                                                : ""
                                                        }
                                                    >
                                                        {subItem.icon}
                                                    </ListItemIcon>
                                                    {open ? (
                                                        <ListItemText
                                                            primary={subItem.label || subItem.name}
                                                            primaryTypographyProps={{
                                                                fontSize: 13.5,
                                                                fontWeight: subActive ? 700 : 500,
                                                            }}
                                                        />
                                                    ) : null}
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                                </Collapse>
                            ) : null}
                        </Box>
                    );
                })}
            </List>
        </Box>
    );
};

export default memo(LeftNav);
